import React, { useContext, useState, useCallback } from 'react';
import styles from './WidgetCardContainer.module.scss';
import { SFCard, SFChip } from 'sfui';
import { MediaContext } from 'ui-smartforce-settings';
import {
  AnalyticsPeriod,
  AnalyticsPeriodType,
  Widget
} from '../../../Models/Widget';
import { ClusterMap } from '../Widgets/ClusterMap/ClusterMap';
import { Doughnut } from '../Widgets/Doughnut/Doughnut';
import { FilterMap } from '../Widgets/FilterMap/FilterMap';
import { GradientLine } from '../Widgets/GradientLine/GradientLine';
import { HeatMap } from '../Widgets/HeatMap/HeatMap';
import { PatternMap } from '../Widgets/PatternMap/PatternMap';
import { RateCard } from '../Widgets/RateCard/RateCard';
import { Sankey } from '../Widgets/Sankey/Sankey';
import { GroupedBar } from '../Widgets/GroupedBar/GroupedBar';
import { StackedLine } from '../Widgets/StackedLine/StackedLine';
import { NoData } from '../Widgets/NoData/NoData';
import {
  isValueEmpty,
  downloadCanvas,
  onShareWidgetClone,
  toCanvas,
  replaceSpecialChars
} from '../../../Helpers';
import { WidgetCardHeader } from '../WidgetCardHeader/WidgetCardHeader';
import {
  WIDGET_CLUSTER_MAP,
  WIDGET_DOUGHNUT,
  WIDGET_FILTER_MAP,
  WIDGET_GRADIENT_LINE,
  WIDGET_GROUPED_BAR,
  WIDGET_HEAT_MAP,
  WIDGET_PATTERN_MAP,
  WIDGET_RANKING_CARD,
  WIDGET_RATE_CARD,
  WIDGET_SANKEY,
  WIDGET_STACKED_LINE
} from '../../../Constants/Analytics';
import { RankingCard } from '../Widgets/RankingCard/RankingCard';
import { SmartforceLegend } from '../SmartforceLegend/SmartforceLegend';

function getLimitedResponseTooltipTitle(
  widget: Widget,
  reportsLength: number
): string {
  if (widget.component.name === 'pattern_map' && widget.component.limited) {
    return `Limited to the last ${widget.component.config.mapData.length} reports.`;
  } else {
    return `Based on the ${reportsLength} most significant reports that occurred during the selected period.`;
  }
}

function checkShowResponseInfo(
  widget: Widget,
  isResponseLimited: boolean
): boolean {
  return (
    isResponseLimited &&
    (widget.component.name === 'cluster_map' ||
      widget.component.name === 'filter_map' ||
      widget.component.name === 'heat_map' ||
      widget.component.name === 'pattern_map') &&
    !isValueEmpty(widget.component.config.mapData)
  );
}

function getInfoMessage(
  widget: Widget,
  isResponseLimited: boolean,
  reportsLength: number,
  period: AnalyticsPeriodType
): string | undefined {
  if (checkShowResponseInfo(widget, isResponseLimited)) {
    return getLimitedResponseTooltipTitle(widget, reportsLength);
  } else if (widget.component.name === 'gradient_line') {
    let label = '';
    if (period === 'year') {
      label = 'months';
    } else if (period === 'half_year') {
      label = 'fortnights';
    } else if (period === 'quarter') {
      label = 'weeks';
    } else {
      label = 'dates';
    }
    return `X-axis values correspond to the filtered ${label}`;
  }
}

function getWidgetFileName(widget: Widget, period: AnalyticsPeriod): string {
  return `${period.from.substring(0, 10)}_${period.to.substring(
    0,
    10
  )}_${widget.component.name.replace(/_/g, '-')}_${replaceSpecialChars(
    widget.title,
    '-'
  )}`;
}

function isWithPadding(
  componentName: string,
  isWidgetDataEmpty: boolean
): boolean {
  return (
    (componentName !== WIDGET_GRADIENT_LINE &&
      componentName !== WIDGET_SANKEY) ||
    isWidgetDataEmpty
  );
}

function hasDrawerOption(componentName: string): boolean {
  return (
    componentName === WIDGET_FILTER_MAP ||
    componentName === WIDGET_PATTERN_MAP ||
    componentName === WIDGET_STACKED_LINE ||
    componentName === WIDGET_GROUPED_BAR
  );
}

function getDrawerOptionLabel(componentName: string): string {
  switch (componentName) {
    case WIDGET_PATTERN_MAP:
      return 'See pattern';
    default:
      return 'Filter by';
  }
}

function checkEmtpyDataWidget(widget: Widget): boolean {
  if (
    widget.component.name === WIDGET_CLUSTER_MAP ||
    widget.component.name === WIDGET_FILTER_MAP ||
    widget.component.name === WIDGET_HEAT_MAP ||
    widget.component.name === WIDGET_PATTERN_MAP
  ) {
    return isValueEmpty(widget.component.config.mapData);
  } else if (
    widget.component.name === WIDGET_DOUGHNUT ||
    widget.component.name === WIDGET_GRADIENT_LINE
  ) {
    return isValueEmpty(widget.component.config.data);
  } else if (widget.component.name === WIDGET_RANKING_CARD) {
    return isValueEmpty(widget.component.config.ranking_list);
  } else if (widget.component.name === WIDGET_RATE_CARD) {
    return isValueEmpty(widget.component.config.percentage);
  } else if (widget.component.name === WIDGET_SANKEY) {
    return (
      isValueEmpty(widget.component.config.nodes) ||
      isValueEmpty(widget.component.config.links)
    );
  } else if (
    widget.component.name === WIDGET_GROUPED_BAR ||
    widget.component.name === WIDGET_STACKED_LINE
  ) {
    return (
      !widget.component.config.data || widget.component.config.data.length === 0
    );
  } else return true;
}

export interface WidgetCardContainerProps {
  analyticsWidgetsContext: React.Context<string>;
  widget: Widget;
  isActive?: boolean;
  period: AnalyticsPeriod;
  isResponseLimited: boolean;
  reportsLength: number;
  isMaximize?: boolean;
  onToggleMaximize?: (isMaximize: boolean) => void;
  onAdd?: (id: number) => void;
  onRemove?: (id: number) => void;
}

export const WidgetCardContainer = ({
  analyticsWidgetsContext,
  widget,
  isActive = false,
  period,
  isResponseLimited,
  reportsLength,
  isMaximize,
  onToggleMaximize,
  onAdd,
  onRemove
}: WidgetCardContainerProps): React.ReactElement<WidgetCardContainerProps> => {
  const { isPhone } = useContext(MediaContext);
  const dateFilterChipLabel = useContext(analyticsWidgetsContext);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const widgetElementRef = React.useRef<HTMLElement>();

  const drawerOption =
    isPhone && hasDrawerOption(widget.component.name)
      ? {
          label: getDrawerOptionLabel(widget.component.name),
          onClick: () => setIsDrawerOpen(true)
        }
      : undefined;

  const onDrawerClose = () => setIsDrawerOpen(false);

  const onShare = async () => {
    if (widgetElementRef.current) {
      const canvas = await toCanvas(widgetElementRef.current, onCanvasClone);
      const fileName = getWidgetFileName(widget, period);
      downloadCanvas(canvas, fileName);
    }
  };

  const onCanvasClone = useCallback(
    (_d: Document, element: HTMLElement) => {
      onShareWidgetClone(element, !widget.component.name.includes('map'));
    },
    [widget.component.name]
  );

  const rowFooter =
    widget.component.name === 'gradient_line' ||
    widget.component.name === 'sankey';

  const wideWidget =
    widget.component.name !== 'rate_card' &&
    widget.component.name !== 'doughnut' &&
    widget.component.name !== 'ranking_card';

  const isWidgetDataEmpty = checkEmtpyDataWidget(widget);

  return (
    <SFCard
      className={styles.card}
      containerClassName={styles.widgetCardContainer}
      sfElevation={2}
      ref={widgetElementRef}
    >
      <WidgetCardHeader
        title={widget.title}
        isActive={isActive}
        drawerMenuOption={drawerOption}
        infoMessage={getInfoMessage(
          widget,
          isResponseLimited,
          reportsLength,
          period.name
        )}
        isMaximize={isMaximize}
        onAdd={() => onAdd && onAdd(widget.id)}
        onRemove={() => onRemove && onRemove(widget.id)}
        onShare={!isWidgetDataEmpty ? onShare : undefined}
        onToggleMaximize={onToggleMaximize}
      />

      <div
        className={`${styles.content} ${
          isWithPadding(widget.component.name, isWidgetDataEmpty)
            ? styles.isWithPadding
            : ''
        }`}
      >
        <>
          {isWidgetDataEmpty && (
            <NoData
              type={widget.component.name}
              link={widget.help_url}
              isContentOmitted={isResponseLimited}
            />
          )}

          {!isWidgetDataEmpty && (
            <>
              {widget.component.name === WIDGET_GROUPED_BAR && (
                <GroupedBar
                  title={widget.title}
                  data={widget.component.config}
                  isDrawerOpen={isDrawerOpen}
                  onDrawerClose={onDrawerClose}
                />
              )}

              {widget.component.name === WIDGET_CLUSTER_MAP && (
                <ClusterMap data={widget.component.config.mapData} />
              )}

              {widget.component.name === WIDGET_DOUGHNUT && (
                <Doughnut data={widget.component.config.data} />
              )}

              {widget.component.name === WIDGET_FILTER_MAP && (
                <FilterMap
                  title={widget.title}
                  data={widget.component.config.mapData}
                  options={widget.component.config.filterMapOptions}
                  isDrawerOpen={isDrawerOpen}
                  onDrawerClose={onDrawerClose}
                />
              )}

              {widget.component.name === WIDGET_GRADIENT_LINE && (
                <GradientLine
                  {...widget.component.config}
                  periodType={period.name}
                />
              )}

              {widget.component.name === WIDGET_HEAT_MAP && (
                <HeatMap data={widget.component.config.mapData} />
              )}

              {widget.component.name === WIDGET_PATTERN_MAP && (
                <PatternMap
                  title={widget.title}
                  data={widget.component.config.mapData}
                  isDrawerOpen={isDrawerOpen}
                  onDrawerClose={onDrawerClose}
                />
              )}

              {widget.component.name === WIDGET_RANKING_CARD && (
                <RankingCard {...widget.component.config} />
              )}

              {widget.component.name === WIDGET_RATE_CARD && (
                <RateCard {...widget.component.config} />
              )}

              {widget.component.name === WIDGET_SANKEY && (
                <Sankey {...widget.component.config} />
              )}

              {widget.component.name === WIDGET_STACKED_LINE && (
                <StackedLine
                  title={widget.title}
                  config={widget.component.config}
                />
              )}
            </>
          )}
        </>

        <div
          className={`${styles.footer} ${rowFooter ? styles.rowFooter : ''} ${
            wideWidget ? styles.wideFooter : ''
          }`}
        >
          <SFChip
            className={styles.footerChip}
            label={dateFilterChipLabel}
            sfColor="primary"
            size="small"
            variant="outlined"
          />
          <SmartforceLegend className={styles.footerLegend} />
        </div>
      </div>
    </SFCard>
  );
};
